import { ScreenshotStorageProvider, SceneUpdateProvider } from '@/modules/unityScreenshot/types';
import { StorageService, SceneService } from '@/modules/databaseService/types';
import { fetchSceneRow } from '@/modules/databaseService/scenes';
import { editCount, offline } from '@/infrastructure/api/v1/client';
import { landedWithin } from '@/infrastructure/api/v1/offlineSaves';
import { objectPathOf } from '@/utils/storageAddress';

/** The bucket the cover screenshots are stored in, and the folder each scene's are in. */
const COVER_BUCKET = 'image_files';
const coverFolder = (sceneInstanceId: string) => `scene-cover-images/${sceneInstanceId}/`;

/**
 * The path of a cover screenshot stored for this scene, from its address in any form; null for anything else (a cover
 * another scene's folder holds, an image the creator uploaded, someone else's address), which is never deleted here.
 */
export function coverScreenshotPath(sceneInstanceId: string, address: string): string | null {
  const path = objectPathOf(address, COVER_BUCKET);
  return path && path.startsWith(coverFolder(sceneInstanceId)) && !path.slice(coverFolder(sceneInstanceId).length).includes('/') ? path : null;
}

export class ScreenshotStorageAdapter implements ScreenshotStorageProvider {
  constructor(private readonly storage: StorageService) {}

  async uploadScreenshot(params: {
    sceneInstanceId: string;
    blob: Blob;
    format: string;
  }): Promise<{ url: string; error?: Error }> {
    try {
      // A new name for every screenshot, so its address is new and nothing cached is stale wherever files are: Supabase
      // keeps the name asked (and the backend drops a cache query when it stores an address), R2 versions it as well. The
      // one before is deleted once this one is the scene's cover.
      const filename = `scene-cover-${crypto.randomUUID().slice(0, 8)}.${params.format}`;

      // Use image_files bucket with scene-cover-images folder
      const filePath = `${coverFolder(params.sceneInstanceId)}${filename}`;

      const { data: stored, error: uploadError } = await this.storage.uploadFile(filePath, params.blob, {
        contentType: `image/${params.format}`,
        cacheControl: '3600',
        upsert: true
      });

      if (uploadError || !stored) {
        return { url: '', error: uploadError ?? new Error('Upload answered no address') };
      }

      // The address the upload answered, never one built from the name asked for
      return { url: stored.url };
    } catch (error) {
      return { url: '', error: error instanceof Error ? error : new Error('Upload failed') };
    }
  }

  async deleteScreenshot(sceneInstanceId: string, address: string, current: string): Promise<void> {
    const path = coverScreenshotPath(sceneInstanceId, address);
    // Never the cover the scene points at now: where storage keeps the name asked (Supabase's), the new cover replaced the
    // old file in place, and their addresses differ only by a cache query (`?t=`) or the form they take
    if (!path || path === objectPathOf(current, COVER_BUCKET)) return;
    const { error } = await this.storage.deleteFile(path);
    // Left behind: it costs a little storage, and nothing points at it
    if (error) console.warn('[screenshots] the previous cover could not be deleted', path, error);
  }
}

export class SceneUpdateAdapter implements SceneUpdateProvider {
  constructor(private readonly sceneService: SceneService) {}

  async sceneCoverImage(sceneInstanceId: string): Promise<string | null> {
    const { data, error } = await fetchSceneRow(sceneInstanceId);
    if (error) throw error;
    return data.scene_instance_cover_img_url || null;
  }

  async updateSceneCoverImage(sceneInstanceId: string, imageUrl: string): Promise<boolean> {
    // The editor's saves are answered on the device and sent behind the ones before them: wait for this one to land
    const since = offline.made();
    await this.sceneService.updateSceneCoverImage(sceneInstanceId, imageUrl);
    return landedWithin(offline, since);
  }

  editCount(): number {
    return editCount();
  }
}
